'use client';

import { useMemo } from 'react';
import type { PlannerDayCounsel } from '@/entities/visit-counsel/model/usePlannerDayCounsels';
import type { DepartureLocationInfo, OrderStatus } from '@/entities/order/model/order.types';
import {
  ORDER_MAP_MARKER_NO_LABEL_BADGE,
  shouldUseOrderMapMarkerNoLabelStyle,
} from '@/entities/order/model/order.mapMarkerPolicy';
import type { Marker, Arrow } from '@/shared/ui/display/MapboxMap';
import { formatScheduleTimeRangeKo } from '@/shared/lib/timeslot/timeslot.types';

const COLOR_LIGHT = {
  primary: '#055cf5',
  other: '#bdbdbd',
  line: '#22c55e',
} as const;

const COLOR_DARK = {
  primary: '#055cf5',
  other: '#6c7086',
  line: '#f7e06e',
} as const;

function scheduleTimeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** scheduleTime → 마커 배지 (서버 시간 - 30분): 13:30→"13", 14:00→"13½" */
function formatScheduleTimeToBadge(scheduleTime: string): string {
  const [h, m] = scheduleTime.split(':').map(Number);
  const totalMins = (h ?? 0) * 60 + (m ?? 0);
  const startMins = Math.max(0, totalMins - 30);
  const hour = Math.floor(startMins / 60);
  const minute = startMins % 60;
  if (minute === 30) return `${hour}½`;
  return String(hour);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function createPopupContent(tags: string[], address: string): string {
  return `<div style="font-family:inherit;line-height:1.4;min-width:140px">
    <div class="mapbox-popup-tags">${tags.map((t) => `#${t}`).join(' ')}</div>
    <div class="mapbox-popup-address">${escapeHtml(address)}</div>
  </div>`;
}

/**
 * 주문 상세 지도용 마커·간선 생성 훅
 * - 상담 배정 시: 해당일 전체 상담 마커 + scheduleTime 순 화살표
 * - 미배정 시: 출발지만 마커
 * - 라벨/뱃지: shouldUseOrderMapMarkerNoLabelStyle 정책에 따라 통일
 */
export function useOrderMapMarkers(
  counsels: PlannerDayCounsel[],
  assignedDepartureLocation: DepartureLocationInfo | null | undefined,
  themeMode: 'light' | 'dark' = 'light',
  /** 출발지만 표시할 때 사용 (counsels 없을 때) */
  fallbackOrder?: { id: string; misoOrderId: string } | null,
  orderStatus?: OrderStatus['type'],
  /** true면 마커 배지를 상담순서(1,2,3) 대신 상담시간(13, 15½)으로 표시 */
  viewByTime?: boolean,
  /** true면 마커 번호를 플래너별로 1,2,3... 매김 (상담지도 페이지용) */
  numberingPerPlanner?: boolean,
  /** 설정 시 해당 플래너의 이동동선만 화살표로 표시 (마커 클릭 시) */
  selectedPlannerIdForArrows?: string | null,
  /** 팝업 링크 URL 오버라이드 (visitCounselId → URL) */
  markerLinkResolver?: (visitCounselId: string) => string | undefined,
  /** 플래너 ID별 핀 색 (상담 지도 등). 설정 시 counselorId로 적용, 이동동선 색도 동일 톤으로 맞춤 */
  plannerColorById?: Record<string, string>
): { markers: Marker[]; arrows: Arrow[] } {
  const COLOR = themeMode === 'dark' ? COLOR_DARK : COLOR_LIGHT;
  const plannerColors = plannerColorById;

  // markers는 selectedPlannerIdForArrows에 의존하지 않음 → 클릭 시 참조 유지로 마커 리렌더 방지
  const markers = useMemo(() => {
    const appointment = counsels.find((c) => c.type === 'current');
    const useNoLabelStyle = shouldUseOrderMapMarkerNoLabelStyle(!!appointment, orderStatus);
    const result: Marker[] = [];

    if (counsels.length > 0) {
      let withLocation = counsels.filter((c) => c.location);

      if (numberingPerPlanner) {
        withLocation = [...withLocation].sort((a, b) => {
          const aid = a.counselorId ?? '';
          const bid = b.counselorId ?? '';
          if (aid !== bid) return aid.localeCompare(bid);
          return (a.scheduleTime ?? '').localeCompare(b.scheduleTime ?? '');
        });
      }

      const plannerSeqMap = new Map<string, number>();
      withLocation.forEach((c, index) => {
        const tag =
          c.type === 'current'
            ? ['출발지', '상담위치', formatScheduleTimeRangeKo(c.scheduleTime)]
            : c.type === 'previous'
              ? ['직전상담', formatScheduleTimeRangeKo(c.scheduleTime)]
              : ['상담', formatScheduleTimeRangeKo(c.scheduleTime)];

        const plannerPin =
          plannerColors && c.counselorId ? plannerColors[c.counselorId] : undefined;
        const color = plannerPin ?? (c.type === 'current' ? COLOR.primary : COLOR.other);
        const useNoLabelForMarker = useNoLabelStyle && c.type === 'current';

        let badge: string;
        if (useNoLabelForMarker) {
          badge = ORDER_MAP_MARKER_NO_LABEL_BADGE;
        } else if (viewByTime) {
          badge = formatScheduleTimeToBadge(c.scheduleTime);
        } else if (numberingPerPlanner && c.counselorId) {
          const seq = (plannerSeqMap.get(c.counselorId) ?? 0) + 1;
          plannerSeqMap.set(c.counselorId, seq);
          badge = String(seq);
        } else {
          badge = String(index + 1);
        }

        result.push({
          id: c.visitCounselId,
          longitude: c.location!.longitude,
          latitude: c.location!.latitude,
          popupContent: createPopupContent(tag, c.location!.roadAddress),
          orderId: c.orderId,
          misoOrderId: c.misoOrderId,
          popupLinkTo: markerLinkResolver?.(c.visitCounselId),
          color,
          markerBadge: badge,
          markerLabel: useNoLabelForMarker ? '' : (c.counselorName ?? ''),
        });
      });
    } else if (assignedDepartureLocation) {
      const loc = assignedDepartureLocation;
      result.push({
        id: 'departure',
        longitude: loc.longitude,
        latitude: loc.latitude,
        popupContent: createPopupContent(['출발지'], loc.roadAddress),
        orderId: fallbackOrder?.id,
        misoOrderId: fallbackOrder?.misoOrderId,
        color: COLOR.primary,
        markerBadge: ORDER_MAP_MARKER_NO_LABEL_BADGE,
      });
    }

    return result;
  }, [
    counsels,
    assignedDepartureLocation,
    themeMode,
    fallbackOrder?.id,
    fallbackOrder?.misoOrderId,
    orderStatus,
    viewByTime,
    numberingPerPlanner,
    markerLinkResolver,
    plannerColors,
  ]);

  const arrows = useMemo(() => {
    const result: Arrow[] = [];
    if (counsels.length === 0) return result;

    let withLocation = counsels.filter((c) => c.location);
    if (numberingPerPlanner) {
      withLocation = [...withLocation].sort((a, b) => {
        const aid = a.counselorId ?? '';
        const bid = b.counselorId ?? '';
        if (aid !== bid) return aid.localeCompare(bid);
        return (a.scheduleTime ?? '').localeCompare(b.scheduleTime ?? '');
      });
    }

    const arrowsSource = selectedPlannerIdForArrows
      ? withLocation.filter((c) => c.counselorId === selectedPlannerIdForArrows)
      : numberingPerPlanner
        ? []
        : withLocation;

    for (let i = 0; i < arrowsSource.length - 1; i++) {
      const from = arrowsSource[i]!;
      const to = arrowsSource[i + 1]!;
      if (from.counselorId && to.counselorId && from.counselorId !== to.counselorId) {
        continue;
      }
      const diffMinutes =
        scheduleTimeToMinutes(to.scheduleTime) - scheduleTimeToMinutes(from.scheduleTime);
      const lineColor =
        plannerColors && from.counselorId && plannerColors[from.counselorId]
          ? plannerColors[from.counselorId]
          : COLOR.line;
      result.push({
        from: from.visitCounselId,
        to: to.visitCounselId,
        color: lineColor,
        width: 4,
        useRoute: true,
        dashed: diffMinutes >= 120,
      });
    }
    return result;
  }, [counsels, themeMode, numberingPerPlanner, selectedPlannerIdForArrows, plannerColors]);

  return { markers, arrows };
}
